import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import MapErrorBoundary from './MapErrorBoundary';
import MeetingMapPinCluster from './MeetingMapPinCluster';
import { fetchGeocode, buildRoomGeocodeQuery, spreadCoordinate } from '../utils/meetingMapGeocode';
import { cityOverviewZoom } from '../utils/mapPinCluster';
import 'leaflet/dist/leaflet.css';
import './MeetingSearchResultsMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MAP_TILE = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
};

const parseCoord = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const getMeetingRoomCoordinates = (room) => {
  if (!room) return null;
  const loc = room.locationCoords;
  if (loc && typeof loc === 'object') {
    const lat = parseCoord(loc.lat);
    const lng = parseCoord(loc.lng ?? loc.lon);
    if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
      return { lat, lng };
    }
  }
  const pairs = [
    [room.latitude, room.longitude],
    [room.lat, room.lng],
  ];
  for (const [a, b] of pairs) {
    const lat = parseCoord(a);
    const lng = parseCoord(b);
    if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
      return { lat, lng };
    }
  }
  return null;
};

/** Spread pins that share the same geocoded location so they don't stack */
const spreadDuplicatePins = (pins) => {
  const groups = new Map();
  pins.forEach((pin) => {
    const key = `${pin.lat.toFixed(5)},${pin.lng.toFixed(5)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(pin);
  });

  const result = [];
  groups.forEach((group) => {
    if (group.length === 1) {
      result.push(group[0]);
      return;
    }
    group.forEach((pin, idx) => {
      const spread = spreadCoordinate(pin.lat, pin.lng, idx, group.length);
      result.push({ ...pin, lat: spread.lat, lng: spread.lng });
    });
  });
  return result;
};

const FitBounds = ({ points, defaultCenter, pinCount }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, {
        padding: [48, 48],
        maxZoom: cityOverviewZoom(pinCount),
      });
    } else if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    } else if (defaultCenter) {
      map.setView([defaultCenter.lat, defaultCenter.lng], 12);
    }
  }, [map, points, defaultCenter, pinCount]);
  return null;
};

const MapResize = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    const invalidate = () => map.invalidateSize({ animate: false, pan: false });
    invalidate();
    const t = setTimeout(invalidate, 120);
    return () => clearTimeout(t);
  }, [map, trigger]);
  return null;
};

const resolveRoomPin = async (room, index, search, center, total) => {
  const id = room._id || room.id;
  let coords = getMeetingRoomCoordinates(room);

  if (!coords) {
    const query = buildRoomGeocodeQuery(room, search);
    coords = await fetchGeocode(query);
    if (!coords && search) {
      coords = await fetchGeocode(`${room.name || 'Meeting room'}, ${search}, India`);
    }
    if (!coords && center) {
      coords = spreadCoordinate(center.lat, center.lng, index, total);
    }
  } else if (total > 1) {
    coords = spreadCoordinate(coords.lat, coords.lng, index, total);
  }

  if (!coords) return null;

  const price = Number(room.pricePerHour) || 0;
  const label =
    price > 0 ? `₹${Math.round(price).toLocaleString('en-IN')}/hr` : 'On request';

  return {
    room,
    id,
    lat: coords.lat,
    lng: coords.lng,
    label,
    name: room.name || 'Room',
    image: room.image,
  };
};

const MeetingSearchResultsMap = ({
  rooms = [],
  selectedRoomId,
  onSelectRoom,
  searchLocation = '',
  defaultCenter = { lat: 12.9716, lng: 77.5946 },
}) => {
  const [mapSearch, setMapSearch] = useState('');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [pins, setPins] = useState([]);
  const [geocoding, setGeocoding] = useState(false);

  const roomsKey = useMemo(
    () => rooms.map((r) => `${r.id || r._id}-${r.pricePerHour}`).join('|'),
    [rooms]
  );

  useEffect(() => {
    let cancelled = false;

    const loadPins = async () => {
      setGeocoding(true);
      setPins([]);
      const search = String(searchLocation || '').trim();

      let center = defaultCenter;
      if (search) {
        const cityCoords = await fetchGeocode(`${search}, India`);
        if (cityCoords && !cancelled) {
          center = cityCoords;
          setMapCenter(cityCoords);
        }
      } else if (!cancelled) {
        setMapCenter(defaultCenter);
        center = defaultCenter;
      }

      const resolved = [];
      const BATCH = 3;
      for (let i = 0; i < rooms.length; i += BATCH) {
        if (cancelled) return;
        const batch = rooms.slice(i, i + BATCH);
        const batchPins = await Promise.all(
          batch.map((room, j) => resolveRoomPin(room, i + j, search, center, rooms.length))
        );
        resolved.push(...batchPins);
      }

      if (!cancelled) {
        setPins(spreadDuplicatePins(resolved.filter(Boolean)));
        setGeocoding(false);
      }
    };

    loadPins();
    return () => {
      cancelled = true;
    };
  }, [roomsKey, searchLocation, defaultCenter.lat, defaultCenter.lng, rooms]);

  const filtered = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q) return pins;
    return pins.filter((m) => {
      const hay = `${m.name} ${m.room.location || ''} ${m.room.city || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pins, mapSearch]);

  const points = pins.map((m) => ({ lat: m.lat, lng: m.lng }));

  const handlePinClick = useCallback(
    (pin) => {
      onSelectRoom?.(pin.room);
    },
    [onSelectRoom]
  );

  return (
    <aside className="msr-map-panel">
      <div className="msr-map-search">
        <input
          type="text"
          value={mapSearch}
          onChange={(e) => setMapSearch(e.target.value)}
          placeholder="Search on map"
          aria-label="Search on map"
        />
      </div>
      <div className="msr-map-wrap">
        {geocoding && (
          <div className="msr-map-loading">
            <span className="msr-map-loading-dot" aria-hidden />
            Placing rooms on map…
          </div>
        )}
        <MapErrorBoundary>
          <MapContainer
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={12}
            className="msr-leaflet"
            scrollWheelZoom
          >
            <TileLayer url={MAP_TILE.url} attribution={MAP_TILE.attribution} />
            <MapResize trigger={`${pins.length}-${geocoding}`} />
            <FitBounds points={points} defaultCenter={mapCenter} pinCount={pins.length} />
            <MeetingMapPinCluster
              pins={filtered}
              selectedRoomId={selectedRoomId}
              onPinClick={handlePinClick}
            />
          </MapContainer>
        </MapErrorBoundary>
        {!geocoding && !pins.length && rooms.length > 0 && (
          <div className="msr-map-empty">
            <p>Could not place rooms on map. Check addresses in room listings.</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default MeetingSearchResultsMap;
